import {
  Alert,
  Item,
  ItemHeader,
  ItemTitle,
  ScrollArea,
  Sentinel,
  Skeleton,
  Spinner,
  useInfiniteScroll,
} from '@ibsys/kit';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useInfiniteEmployeeGroups } from '@/entities/employeeGroups';
import { useAppNavigate } from '@/shared/lib';

type Props = {
  searchQuery?: string;
};

/**
 * Левая панель страницы групп операторов: бесконечная прокрутка,
 * выделение активной группы по :id.
 */
export const EmployeeGroupsList: FC<Props> = ({ searchQuery }) => {
  const { t } = useTranslation(['employees']);
  const { id } = useParams<{ id?: string }>();
  const navigate = useAppNavigate();
  const viewportRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteEmployeeGroups({
    limit: 50,
    name: searchQuery,
  });
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: fetchNextPage,
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    threshold: 500,
    root: viewportRef,
  });

  const groups = data.groups;

  const handleSelect = (groupId: number) => {
    navigate(`/employee-groups/${groupId}`);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-14" />
        ))}
      </div>
    );
  }

  if (!groups.length) {
    return (
      <div className="w-full">
        <Alert className="text-muted-foreground p-4" title={t('employees:groups.noGroups')} />
      </div>
    );
  }

  return (
    <ScrollArea className="pb-2" viewPortProps={{ ref: viewportRef }}>
      <ul className="flex flex-col gap-4">
        {groups.map((group) => {
          const isActive = group.id === Number(id);
          return (
            <li key={group.id} onClick={() => handleSelect(group.id)}>
              <Item variant={isActive ? 'muted' : 'outline'} className="hover:bg-muted cursor-pointer">
                <ItemHeader>
                  <ItemTitle>{group.name}</ItemTitle>
                </ItemHeader>
              </Item>
            </li>
          );
        })}
        <Sentinel ref={sentinelRef}>
          {isFetchingNextPage && (
            <div className="w-full flex justify-center py-6">
              <Spinner className="size-8" />
            </div>
          )}
        </Sentinel>
      </ul>
    </ScrollArea>
  );
};
